import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useApiAdminsalesHierarchiesIdDelete } from '@r/api/generated/client.ts';
import type { SalesHierarchySalesHierarchyReadModel } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { invalidateSalesHierarchiesQueries } from '@admin/components/hierarchy/hierarchy-utils.ts';

type SalesHierarchy = SalesHierarchySalesHierarchyReadModel;

/**
 * Gère la suppression d'une hiérarchie : affiche une confirmation, supprime la
 * hiérarchie puis invalide la liste des hiérarchies.
 */
export function useDeleteSalesHierarchy(onDeleted?: () => void) {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const queryClient = useQueryClient();

  const deleteMutation = useApiAdminsalesHierarchiesIdDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t('admin.hierarchy.delete.success'));
        invalidateSalesHierarchiesQueries(queryClient);
        onDeleted?.();
      },
      onError: (error) => {
        if (isOrvalErrorFromPath(error, 'delete', '/admin/sales-hierarchies')) {
          showOrvalErrorToast(error, t('admin.hierarchy.delete.error'));
        } else {
          toast.error(t('admin.hierarchy.delete.error'));
        }
      }
    }
  });

  const handleDelete = (hierarchy: SalesHierarchy) => {
    toast(t('admin.hierarchy.delete.confirm', { name: hierarchy.name }), {
      action: {
        label: t('label.confirm'),
        onClick: () => deleteMutation.mutate({ id: String(hierarchy.id) })
      },
      cancel: {
        label: t('label.cancel'),
        onClick: () => {}
      }
    });
  };

  return { handleDelete };
}
